import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import cn from 'classnames';
import { TweenLite, Cubic as Ease } from 'gsap';
import round from '../../helpers/round';
import theme from '../../styles/theme';

class DigitTweener {
  animated = true;

  tweenAt = 0;

  constructor(currentEl, nextEl) {
    this.currentEl = currentEl;
    this.nextEl = nextEl;
    this.onTweenComplete();
  }

  onTweenComplete = () => {
    // swap current and next elements, ready for the next transition
    [this.nextEl, this.currentEl] = [this.currentEl, this.nextEl];
    this.nextEl.style.transform = 'rotate3d(1, 0, 0, 90deg)';
  };

  get t() {
    return this.tweenAt;
  }

  set t(t) {
    this.tweenAt = t;
    this.nextEl.style.transform = `rotate3d(1, 0, 0, ${round((1 - t) * 90)}deg)`;
    this.currentEl.style.transform = `rotate3d(1, 0, 0, ${round(t * -90)}deg)`;
  }

  set value(value) {
    if (this.animated) {
      const toVars = { t: 1, ease: Ease.easeInOut, onComplete: this.onTweenComplete };
      if (!this.nextEl.innerHTML && !this.currentEl.innerHTML) {
        // when we set the initial value, it looks good if we stagger the individual digits a bit
        toVars.delay = Math.random() * 0.3;
      }
      this.nextEl.innerHTML = value;
      TweenLite.fromTo(
        this,
        0.4,
        { t: 0 },
        toVars,
      );
    } else {
      this.currentEl.innerHTML = value;
    }
  }
}

class Digit extends PureComponent {
  componentDidUpdate(prevProps) {
    const { animated, value } = this.props;
    if (prevProps.animated !== animated) {
      this.tweener.animated = animated;
    }
    if (prevProps.value !== value) {
      this.tweener.value = value;
    }
  }

  setRef = el => {
    if (el) {
      const { animated, value } = this.props;
      this.tweener = new DigitTweener(el.childNodes[0], el.childNodes[1]);
      this.tweener.animated = animated;
      this.tweener.value = value;
    }
  };

  render() {
    const { classes, className } = this.props;
    return (
      <span className={cn(className, classes.wrapper)} ref={this.setRef}>
        <span className={classes.digit} />
        <span className={classes.digit} />
      </span>
    );
  }
}

Digit.propTypes = {
  animated: PropTypes.bool,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  value: PropTypes.number.isRequired,
};

Digit.defaultProps = {
  animated: true,
  className: undefined,
};

export default injectSheet({
  wrapper: {
    display: 'inline-block',
    position: 'relative',
    width: 24,
    height: 48,
    lineHeight: 1.35,
    textAlign: 'center',
    fontFamily: theme.fonts.bold,
    fontWeight: theme.fonts.boldWeight,
    [theme.breakpoints.up('md')]: {
      width: 32,
      height: 62,
    },
  },
  digit: {
    position: 'absolute',
    left: '-0.15em',
    top: 0,
    width: '1em',
    height: '100%',
    transformOrigin: '50% 50% -0.8em',
    backfaceVisibility: 'hidden',
  },
})(Digit);
